import { AddIcon, Button, Form, FormField, Input, Select, Textarea, useDebounce } from "@rothoblaas/design-system";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import Icon from "../../components/ui/Icon";
import { useAppContext } from "../../context/appContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { InsertCalculationBody } from "../../http/types/calculations";
import { ModuleLight } from "../../http/types/modules";
import { ProjectLight } from "../../http/types/projects";
import { useCalculationsApi } from "../../http/useCalculations";
import { useModulesApi } from "../../http/useModules";
import { useProjectsApi } from "../../http/useProjects";
import { PaginationQuery } from "../../types";
import ProjectModal from "../projects/ProjectModal";
import styles from "./CalculationForm.module.css";

interface CreateCalculationForm {
  module: ModuleLight | null;
  project: ProjectLight | null;
  name: string | null;
  description: string | null;
}

interface CalculationFormProps {
  isAdmin?: boolean; // You can add props here if needed, for example, to handle editing an existing calculation
  disableNavigationOnSuccess?: boolean;
  onSuccess?: (calculation?: { id: string }) => void;
  moduleId?: string; // if exist, users don't view module's select.
  projectId?: string; // if exist, users don't view project's select
  onClose?: () => void;
}

export const CalculationForm: FC<CalculationFormProps> = ({
  isAdmin = false,
  disableNavigationOnSuccess = false,
  onSuccess,
  moduleId,
  projectId,
  onClose,
}: CalculationFormProps) => {
  const i18n = useI18n();
  const { isSingleModuleApp } = useAppContext();

  const aborter = useRef(new AbortController());
  const filters = useListFilters({ isSearchable: true, limit: 400 });
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModalCreateProject, setShowModalCreateProject] = useState(false);
  const calculationsApi = useCalculationsApi();
  const moduleApi = useModulesApi();
  const projectApi = useProjectsApi();
  const urlCalculations = isAdmin ? "/admin/calculations" : "/my-calculations";

  const { data: modulesResponse } = !isAdmin
    ? moduleApi.useModules(query, aborter.current.signal)
    : moduleApi.useAdminModules(query, aborter.current.signal);

  const { data: projectsResponse } = !isAdmin
    ? projectApi.useMyProjects(query, aborter.current.signal, !isSingleModuleApp)
    : projectApi.useAdminProjects(query, aborter.current.signal, !isSingleModuleApp);

  const modules = modulesResponse?.items ?? [];
  const projects = projectsResponse?.items ?? [];
  const fixedProject = projectId ? (projects.find((project) => project.id === projectId) ?? null) : null;

  const onSubmit = () => async (formData: CreateCalculationForm) => {
    setIsSubmitting(true);
    const body: InsertCalculationBody = {
      name: formData.name ?? "",
      description: formData.description!,
      projectId: formData.project?.id ?? undefined,
      moduleId: moduleId ?? formData.module?.id!,
    };

    calculationsApi
      .insertCalculation(body, isAdmin)
      .then((response) => {
        if (!disableNavigationOnSuccess) {
          const projectParam = body.projectId ? `?projectId=${body.projectId}` : "";
          navigate(`${urlCalculations}/${response.id}${projectParam}`);
        }
        onSuccess?.(response);
      })
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  const defaultValues: CreateCalculationForm = {
    module: null,
    project: null,
    name: null,
    description: null,
  };

  return (
    <>
      <Form<CreateCalculationForm>
        defaultValues={defaultValues}
        disabled={isSubmitting}
        submitText={i18n.translation.common.create}
        secondaryButton={
          onClose ? (
            <Button onClick={onClose} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>
          ) : undefined
        }
        onSubmit={onSubmit}
      >
        {({ register, formState: { errors }, control, setValue, watch }) => {
          const selectedProject = watch("project");
          useEffect(() => {
            if (projectId && fixedProject && selectedProject?.id !== fixedProject.id) {
              setValue("project", fixedProject);
            }
          }, [projectId, fixedProject?.id, selectedProject?.id]);

          return (
            <FormFieldsContainer fullWidth>
              {!moduleId && (
                <Controller
                  name="module"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={defaultValues.module}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.modules.singular} error={fieldState.error}>
                      {({ labelId }) => (
                        <Select
                          {...field}
                          inputId={labelId}
                          options={modules ?? []}
                          placeholder={`${i18n.translation.common.select}...`}
                          isDisabled={isSubmitting} //isLoadingModules || !!isErrorModules ||
                          isLoading={isSubmitting} // isLoadingModules ||
                          getOptionValue={(x) => x.id}
                          getOptionLabel={(x) => x.name}
                          isInvalid={fieldState.invalid}
                          onChange={(selectedOption) => setValue("module", selectedOption ?? null)}
                        />
                      )}
                    </FormField>
                  )}
                />
              )}

              {!isSingleModuleApp && (
                <Controller
                  name="project"
                  control={control}
                  defaultValue={defaultValues.project}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.projects.singular} error={fieldState.error} isOptional>
                      {({ labelId }) =>
                        projectId ? (
                          <Input id={labelId} value={fixedProject?.name ?? ""} disabled />
                        ) : (
                          <div className={styles.selectWithButton}>
                            <Select
                              {...field}
                              inputId={labelId}
                              options={projects}
                              placeholder={`${i18n.translation.common.select}...`}
                              isDisabled={isSubmitting}
                              isLoading={isSubmitting}
                              isClearable
                              getOptionValue={(x) => x.id}
                              getOptionLabel={(x) => x.name}
                              isInvalid={fieldState.invalid}
                              onChange={(selectedOption) => setValue("project", selectedOption ?? null)}
                            />
                            <Button
                              title={i18n.translation.projects.createNew}
                              className={styles.button}
                              disabled={isSubmitting}
                              onClick={() => {
                                setShowModalCreateProject(true);
                              }}
                            >
                              <Icon glyph={AddIcon} />
                            </Button>
                          </div>
                        )
                      }
                    </FormField>
                  )}
                />
              )}

              {/* NAME */}
              <FormField label={i18n.translation.common.name} error={errors.name}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isSubmitting}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>

              {/* DESCRIPTION */}
              <FormField label={i18n.translation.common.description} error={errors.description} isOptional>
                {({ labelId, isOptional, isInvalid }) => (
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isSubmitting}
                    {...register("description", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>

              {showModalCreateProject && (
                <ProjectModal
                  onClose={() => setShowModalCreateProject(false)}
                  onSuccess={(projectSelected) => {
                    if (!!projectSelected?.id) {
                      setValue("project", projectSelected);
                    }
                    setShowModalCreateProject(false);
                  }}
                />
              )}
            </FormFieldsContainer>
          );
        }}
      </Form>
    </>
  );
};
